import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Kanban,
  LayoutDashboard,
  Search,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ResponsiveContainer,
} from 'recharts';

import { usePageContext } from '@/hooks/usePageContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { useCRMStages, type CRMStage } from '@/v5/hooks/useCRM';
import { useCRMLeads, type Lead } from '@/v5/hooks/useLeads';
import { buildApiFilterString, useFilters } from '@/hooks/useFilters';
import {
  CRM_DASHBOARD_FILTER_CONFIGS,
  useFacilityOverrideValues,
} from '@/pages/dashboard/dashboardFilters';
import * as crmSvc from '@/services/crm.service';
import type { LeadSourceSlice } from '@/types/crm.types';
import { LeadPerformanceCards } from './LeadPerformanceCards';

/**
 * Categorical hues for lead source categories, validated for colour-vision
 * deficiency against this app's white card surface (worst adjacent pair ΔE 8.9
 * simulated / 23.8 normal vision). The legend's names and percentages are the
 * required relief for the two hues that sit under 3:1 contrast — don't drop it.
 *
 * The residual buckets are deliberately gray: they carry no identity, and a gray
 * sitting in the categorical list fails the chroma floor.
 */
const SOURCE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#ec4899'];
const OTHER_COLOR = '#94a3b8';
const UNSOURCED_COLOR = '#cbd5e1';
const LEADS_SERIES = '#3b82f6';
const CONVERTED_SERIES = '#10b981';

/**
 * The "Visible Lead Queue" stat card is hidden (2026-09-28) while we decide
 * whether to remove it for good. Set to true to bring it back; the card row
 * widens to fit it.
 */
const SHOW_VISIBLE_LEAD_QUEUE_CARD = false;

/**
 * Paint each category from its stable position in the account's configured order
 * so filtering one out never repaints the survivors. Two visible categories can
 * only collide when the account has more than five of them; the second then takes
 * the next free hue, which is still deterministic for a given set of slices.
 */
function assignSliceColors(slices: LeadSourceSlice[]): string[] {
  const used = new Set<number>();

  return slices.map((slice) => {
    if (slice.bucket === 'other') return OTHER_COLOR;
    if (slice.bucket === 'unsourced') return UNSOURCED_COLOR;

    const start = (slice.colorIndex ?? 0) % SOURCE_COLORS.length;
    let hue = start;
    for (let step = 0; step < SOURCE_COLORS.length && used.has(hue); step += 1) {
      hue = (start + step + 1) % SOURCE_COLORS.length;
    }
    used.add(hue);
    return SOURCE_COLORS[hue];
  });
}

function formatDateTime(value: string): string {
  if (!value) return 'No date';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getLeadAttention(lead: Lead): { label: string; needsAttention: boolean } {
  if (!lead.contactDate) {
    return { label: 'No received date', needsAttention: true };
  }

  const parsed = new Date(lead.contactDate);
  if (Number.isNaN(parsed.getTime())) {
    return { label: 'Review lead date', needsAttention: true };
  }

  const diffMs = Date.now() - parsed.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays >= 14) {
    return { label: `Received ${diffDays}d ago`, needsAttention: true };
  }

  if (!lead.assignedTo || lead.assignedTo === 'Unassigned') {
    return { label: 'Needs assignment', needsAttention: true };
  }

  return { label: `Received ${Math.max(diffDays, 0)}d ago`, needsAttention: false };
}

function LoadingCopy({ message }: { message: string }) {
  return <p className="py-6 text-sm text-slate-400">{message}</p>;
}

function EmptyCopy({ message }: { message: string }) {
  return <p className="py-6 text-sm text-slate-400">{message}</p>;
}

function StageSnapshot({ stages }: { stages: CRMStage[] }) {
  if (stages.length === 0) {
    return <EmptyCopy message="No CRM stages are available yet." />;
  }

  const maxCount = Math.max(...stages.map((stage) => stage.planCount), 1);

  return (
    <div className="space-y-3">
      {stages.map((stage) => {
        const width = `${Math.max((stage.planCount / maxCount) * 100, 4)}%`;
        return (
          <div key={`${stage.stageID}-${stage.accountCustomStageID}`} className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{stage.stageName}</p>
              </div>
              <span className="text-sm font-semibold tabular-nums text-slate-900">
                {stage.planCount.toLocaleString('en-US')}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadList({
  leads,
  emptyMessage,
}: {
  leads: Lead[];
  emptyMessage: string;
}) {
  if (leads.length === 0) {
    return <EmptyCopy message={emptyMessage} />;
  }

  return (
    <div className="divide-y divide-slate-100">
      {leads.map((lead) => {
        const attention = getLeadAttention(lead);
        return (
          <div key={lead.contactID} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">
                {lead.firstName} {lead.lastName}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                {lead.planType || 'Unknown plan type'}
                {lead.leadSource ? ` · ${lead.leadSource}` : ''}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                {lead.assignedTo || 'Unassigned'}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-xs text-slate-600">{formatDateTime(lead.contactDate)}</p>
              <p
                className={`mt-1 text-xs font-medium ${
                  attention.needsAttention ? 'text-amber-600' : 'text-slate-500'
                }`}
              >
                {attention.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CRMDashboardPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('CRM Dashboard');
  const filters = useFilters('v5-crm', CRM_DASHBOARD_FILTER_CONFIGS);
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const { filtersArray } = useMemo(() => buildApiFilterString(effectiveValues), [effectiveValues]);

  // Saved filters are a round-trip, so `filters.values` is the empty default on
  // the first render. Everything below waits for them: firing now would spend an
  // unfiltered query on the three most expensive CRM endpoints, show its answer,
  // and then race its own filtered replacement — which is what made a reload
  // look unfiltered until the user touched a filter.
  const filtersReady = !filters.loading;

  const { stages, newLeadsCount, loading: stagesLoading } = useCRMStages(
    filtersArray,
    '',
    filtersReady,
  );
  const { leads, totalCount, loading: leadsFetching } = useCRMLeads(filtersReady, filtersArray);
  // `useCRMLeads` reports "not loading" before it is allowed to start; without
  // this the lead cards flash their empty state while the filters are in flight.
  const leadsLoading = !filtersReady || leadsFetching;

  const { data: charts } = useQuery({
    queryKey: ['crm-dashboard-charts', filtersArray],
    queryFn: () => crmSvc.getCRMDashboardCharts({ filtersArray }),
    staleTime: 60_000,
    enabled: filtersReady,
    // Hold the previous render while refetching on a filter change — no flash.
    placeholderData: keepPreviousData,
  });

  const sourceData = useMemo(() => charts?.leadSources ?? [], [charts]);
  const sourceColors = useMemo(() => assignSliceColors(sourceData), [sourceData]);
  const trendData = useMemo(() => charts?.trend ?? [], [charts]);
  const serviceData = useMemo(() => charts?.planTypes ?? [], [charts]);
  // Percentages come off the API's total, which includes every bucket, so they
  // always sum to 100 regardless of how the tail was folded.
  const sourceTotal = charts?.leadSourceTotal || 1;

  const sortedStages = useMemo(
    () =>
      [...stages]
        .sort((a, b) => Number(a.orderNum || 0) - Number(b.orderNum || 0))
        .slice(0, 6),
    [stages],
  );

  const totalPlans = useMemo(
    () => stages.reduce((sum, stage) => sum + stage.planCount, 0),
    [stages],
  );

  const busiestStage = useMemo(
    () => [...stages].sort((a, b) => b.planCount - a.planCount)[0] ?? null,
    [stages],
  );

  // Same definition as the Plans page's Inquiries card and the Inquiries list
  // (/plans?view=inquiries): the first stage, in pipeline order, whose name
  // contains "inquiry".
  const inquiryStage = useMemo(
    () => stages.find((stage) => stage.stageName.toLowerCase().includes('inquiry')) ?? null,
    [stages],
  );
  const inquiryCount = inquiryStage?.planCount ?? 0;

  const recentLeads = useMemo(
    () =>
      [...leads]
        .sort((a, b) => new Date(b.contactDate).getTime() - new Date(a.contactDate).getTime())
        .slice(0, 5),
    [leads],
  );

  const attentionLeads = useMemo(
    () => leads.filter((lead) => getLeadAttention(lead).needsAttention).slice(0, 5),
    [leads],
  );

  useEffect(() => {
    if (stagesLoading || leadsLoading) return;

    updateAIContext({
      dataSummary: `Showing ${totalPlans} active CRM plans across ${stages.length} stages and ${totalCount} visible leads.`,
      extra: busiestStage
        ? `Top stage is ${busiestStage.stageName} with ${busiestStage.planCount} plans. Inquiries: ${inquiryCount}. New leads: ${newLeadsCount}.`
        : `No CRM stages are currently available. New leads: ${newLeadsCount}.`,
    });
  }, [
    busiestStage,
    inquiryCount,
    leadsLoading,
    newLeadsCount,
    stages.length,
    stagesLoading,
    totalCount,
    totalPlans,
    updateAIContext,
  ]);

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={CRM_DASHBOARD_FILTER_CONFIGS}
          values={effectiveValues}
          options={filters.options}
          onChange={filters.setFilter}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          storageKey="crm-dashboard"
          showAIAssistant={false}
        />
      )}
    >
      {/* `max-md:pb-16` clears the browser's bottom chrome — below `md` the shell releases
          the viewport lock and the document is the scroller. `pb-safe` cannot do it:
          index.html carries no `viewport-fit=cover`, so the inset resolves to 0px. */}
      <div className="space-y-6 overflow-y-auto p-6 max-md:pb-16">
      <PageHeader
        title="CRM"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              leftIcon={<LayoutDashboard className="h-4 w-4" />}
              onClick={() => navigate('/leads')}
            >
              Open Leads
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Kanban className="h-4 w-4" />}
              onClick={() => navigate('/crm/pipeline')}
            >
              Open Pipeline
            </Button>
            <Button
              leftIcon={<UserPlus className="h-4 w-4" />}
              onClick={() => navigate('/leads/new')}
            >
              New Lead
            </Button>
          </div>
        }
      />

      <MetricCardGrid columns={SHOW_VISIBLE_LEAD_QUEUE_CARD ? 5 : 4}>
        <StatCard
          label="Pipeline Plans"
          value={totalPlans}
          subtitle={busiestStage ? `${busiestStage.stageName} is busiest` : 'No stage activity yet'}
          tone="blue"
          icon={<Kanban className="h-4 w-4" />}
          onClick={() => navigate('/crm/pipeline')}
        />
        <StatCard
          label="Inquiries"
          value={inquiryCount}
          subtitle={inquiryStage ? `Plans in ${inquiryStage.stageName}` : 'No inquiry stage set up'}
          tone="sky"
          icon={<Search className="h-4 w-4" />}
          onClick={() => navigate('/plans?view=inquiries')}
        />
        <StatCard
          label="New Leads"
          value={newLeadsCount}
          subtitle="Fresh demand entering CRM"
          tone="emerald"
          icon={<UserPlus className="h-4 w-4" />}
          onClick={() => navigate('/crm/pipeline')}
        />
        {SHOW_VISIBLE_LEAD_QUEUE_CARD && (
          <StatCard
            label="Visible Lead Queue"
            value={totalCount}
            subtitle="Lead records in the current CRM feed"
            tone="violet"
            icon={<Users className="h-4 w-4" />}
            onClick={() => navigate('/leads')}
          />
        )}
        <StatCard
          label="Needs Attention"
          value={attentionLeads.length}
          subtitle="Unassigned or aging visible leads"
          tone="amber"
          icon={<AlertTriangle className="h-4 w-4" />}
          onClick={() => navigate('/crm/pipeline')}
        />
      </MetricCardGrid>

      {/* SCRUM-1445 / SCRUM-1446. A 12-column grid at every width, so at 375px each track's
          floor was its item's min-content width and the row was far wider than the screen —
          "Interested Services" was mostly off-screen until the user scrolled sideways.

          `max-md:grid-cols-1` is NOT sufficient on its own: `grid-column: span 4` on a
          one-track grid CREATES three implicit columns, so each child has to be reset too.
          That pairing is what the test guards, because dropping the `col-span-1` half leaves
          a diff that looks correct and changes nothing. */}
      <div className="grid grid-cols-12 gap-5 max-md:grid-cols-1">
        <div className="col-span-4 max-md:col-span-1">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 pt-4 px-5">
              <span className="text-sm font-semibold">Lead Sources</span>
            </CardHeader>
            <CardBody className="px-5 pb-4">
              {sourceData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No lead data</p>
              ) : (
                /* SCRUM-1445: the card rendered its legend and no chart. `ResponsiveContainer`
                   is given a fixed pixel width here, and it is a flex item with the default
                   `flex-shrink: 1` — so once the card was squeezed to a fraction of 375px the
                   box measured near zero and recharts declines to draw. The legend beside it
                   is `flex-1` with `truncate` labels, which is why that survived and
                   ellipsised while the pie vanished.
                   `shrink-0` keeps the 140px box; stacking below `md` gives the legend its own
                   full-width line instead of ~40px. */
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-start">
                  <div className="shrink-0">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie
                        data={sourceData}
                        cx="50%"
                        cy="50%"
                        outerRadius={65}
                        innerRadius={25}
                        dataKey="count"
                        nameKey="name"
                        stroke="#fff"
                        strokeWidth={2}
                      >
                        {sourceData.map((s, i) => (
                          <Cell key={`${s.bucket}-${s.categoryId ?? s.name}`} fill={sourceColors[i]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 8 }}
                        formatter={(value: number, name: string) => [
                          `${value.toLocaleString()} ${value === 1 ? 'lead' : 'leads'} · ${Math.round((value / sourceTotal) * 100)}%`,
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1.5 max-md:w-full">
                    {sourceData.map((s, i) => (
                      <div key={`${s.bucket}-${s.categoryId ?? s.name}`} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sourceColors[i] }} />
                          <span className="text-[11px] text-muted-foreground truncate" title={s.name}>{s.name}</span>
                        </div>
                        <span className="text-[11px] font-semibold text-foreground flex-shrink-0">
                          {Math.round((s.count / sourceTotal) * 100)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-span-5 max-md:col-span-1">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 pt-4 px-5">
              <span className="text-sm font-semibold">Lead Conversion Trend</span>
              <p className="text-xs text-muted-foreground">Leads created per month, and how many have converted</p>
            </CardHeader>
            <CardBody className="px-3 pb-4">
              {trendData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No lead activity in this range</p>
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={LEADS_SERIES} stopOpacity={0.15} />
                        <stop offset="95%" stopColor={LEADS_SERIES} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="convertedGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CONVERTED_SERIES} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={CONVERTED_SERIES} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="leads" name="Leads created" stroke={LEADS_SERIES} fill="url(#leadsGrad)" strokeWidth={2} fillOpacity={0.6} />
                    <Area type="monotone" dataKey="converted" name="Converted" stroke={CONVERTED_SERIES} fill="url(#convertedGrad)" strokeWidth={2} fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-span-3 max-md:col-span-1">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 pt-4 px-5">
              <span className="text-sm font-semibold">Interested Services</span>
            </CardHeader>
            <CardBody className="px-3 pb-4">
              {serviceData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No service interest recorded</p>
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={serviceData} layout="vertical" margin={{ left: 10 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={90} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    {/* One measure, one hue — colouring nominal bars by value would
                        double-encode bar length as colour. */}
                    <Bar dataKey="count" name="Leads" fill={LEADS_SERIES} radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <LeadPerformanceCards filtersArray={filtersArray} enabled={filtersReady} />

      <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader
            action={
              <Button
                variant="link"
                rightIcon={<ArrowRight className="h-4 w-4" />}
                onClick={() => navigate('/crm/pipeline')}
              >
                View Pipeline
              </Button>
            }
          >
            <CardTitle>Pipeline Snapshot</CardTitle>
          </CardHeader>
          <CardBody>
            {stagesLoading ? (
              <LoadingCopy message="Loading CRM stages..." />
            ) : (
              <StageSnapshot stages={sortedStages} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            action={
              <Button
                variant="link"
                rightIcon={<ArrowRight className="h-4 w-4" />}
                onClick={() => navigate('/leads')}
              >
                View Leads
              </Button>
            }
          >
            <CardTitle>Recent Leads</CardTitle>
          </CardHeader>
          <CardBody>
            {leadsLoading ? (
              <LoadingCopy message="Loading recent leads..." />
            ) : (
              <LeadList
                leads={recentLeads}
                emptyMessage="No recent leads are available right now."
              />
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          action={
            <Button
              variant="link"
              rightIcon={<ArrowRight className="h-4 w-4" />}
              onClick={() => navigate('/crm/pipeline')}
            >
              Review Pipeline
            </Button>
          }
        >
          <CardTitle>Follow-Up Attention</CardTitle>
        </CardHeader>
        <CardBody>
          {leadsLoading ? (
            <LoadingCopy message="Loading leads that need follow-up..." />
          ) : (
            <LeadList
              leads={attentionLeads}
              emptyMessage="Visible leads look healthy. No immediate follow-up gaps were detected."
            />
          )}
        </CardBody>
      </Card>
      </div>
    </ListPageLayout>
  );
}
